@if ({
  initialData: initialData$ | async,
  publishDateOptions: publishDateOptions$ | async,
  todayPublishDateOption: todayPublishDateOption$ | async,
  customerPortalUrl: customerPortalUrl$ | async,
  categoryTree: categoryTree$ | async,
  salaryOptions: salaryOptions$ | async,
  seniorityLevelOptions: seniorityLevelOptions$ | async
}; as data) {

<form [formGroup]="searchForm" class="search-panel">

  <!-- keyword / specialization / location -->
  <div class="search-panel__fields">

    <div class="search-panel__field search-panel__field--keyword">
      <label class="search-panel__field-icon" for="lbl-search">
        <svg-icon src="/assets/icons/search-outline.svg" [svgClass]="'ico-search'"></svg-icon>
      </label>
      <input
        id="lbl-search"
        type="text"
        class="search-panel__input"
        formControlName="searchKeyword"
        [placeholder]="'label.SearchKeyword' | translate"
        (keyup.enter)="onSearchButtonClick(data.salaryOptions, data.publishDateOptions)"
      />
    </div>

    <div
      class="search-panel__field search-panel__field--specialization"
      [class.search-panel__field--active]="specializations.value?.length > 0"
    >
      <hrra-specialization-tree-select
        ngSkipHydration
        formControlName="specializations"
        [PlaceholderText]="'label.SearchWithSpecialization'"
        [SelectItemCountText]="'label.SelectedSpecializationCount'"
        [isLarge]="false"
        [enableVirtualScroll]="true"
        [compressValues]="true"
        [appendTo]="'self'"
      ></hrra-specialization-tree-select>
    </div>

    <div
      class="search-panel__field search-panel__field--location"
      [class.search-panel__field--active]="locations.value?.length > 0"
    >
      <svg-icon
        class="search-panel__field-icon"
        src="/assets/icons/pin.svg"
        [svgClass]="'ico-pin'"
      ></svg-icon>
      <hrra-location-tree-select
        ngSkipHydration
        formControlName="locations"
        [PlaceholderText]="'label.Location'"
        [SelectItemCountText]="'label.SelectedCityCount'"
        [isLarge]="false"
        [enableVirtualScroll]="true"
        [compressValues]="true"
        [appendTo]="'self'"
      ></hrra-location-tree-select>
    </div>

  </div>

  <!-- secondary filters, today shortcut and search button -->
  <div class="search-panel__actions">

    <div class="search-panel__chips">

      <div class="chip chip--industry" [class.chip--active]="industries.value?.length > 0">
        <hrra-industry-tree-select
          ngSkipHydration
          formControlName="industries"
          [PlaceholderText]="'label.IndustryField'"
          [SelectItemCountText]="'label.SelectedIndustryCount'"
          [isLarge]="false"
          [enableVirtualScroll]="true"
          [compressValues]="true"
          [appendTo]="'self'"
        ></hrra-industry-tree-select>
      </div>

      <div
        class="chip chip--seniority-level"
        [class.chip--active]="seniorityLevels.value?.length > 0"
      >
        <p-multiSelect
          #seniorityLevelSelect
          ngSkipHydration
          formControlName="seniorityLevels"
          [options]="data.seniorityLevelOptions"
          optionLabel="name"
          [optionValue]="'code'"
          [placeholder]="'label.SeniorityLevelMain' | translate"
          [showHeader]="false"
          [showToggleAll]="false"
          [showClear]="true"
          [virtualScroll]="false"
          [styleClass]="'chip__dropdown'"
          [panelStyleClass]="'dropdown-panel'"
          [appendTo]="'self'"
        >
          <ng-template let-item #item>
            <span class="dropdown-item">{{ item.name }}</span>
          </ng-template>

          <ng-template #dropdownicon>
            <svg-icon src="/assets/icons/arrow-down.svg" [svgClass]="'ico-caret'"></svg-icon>
          </ng-template>

          <ng-template #clearicon>
            <svg-icon src="/assets/icons/close-small.svg" [svgClass]="'ico-clear'"></svg-icon>
          </ng-template>

          <!-- primeng renders the placeholder itself when nothing is selected, so only the selected state is templated here -->
          <ng-template pTemplate="selectedItems" let-selectedItems>
            @if (selectedItems?.length < maxSelectedValue) {
              @for (item of selectedItems; track item; let last = $last) {
                <span>{{ last ? item.name : item.name + ', ' }}</span>
              }
            } @else if (selectedItems?.length >= maxSelectedValue) {
              <span>{{ 'label.SelectedMultiselectOptions' | translate: { count: selectedItems.length } }}</span>
            }
          </ng-template>

          <ng-template #footer>
            <div class="dropdown-footer">
              <button
                type="button"
                class="dropdown-footer__reset"
                (click)="seniorityLevels.setValue([]); seniorityLevelSelect.hide()"
              >{{ 'label.ClearFilter' | translate }}</button>
              <button
                type="button"
                class="dropdown-footer__approve"
                (click)="seniorityLevelSelect.hide()"
              >{{ 'label.Approve' | translate }}</button>
            </div>
          </ng-template>
        </p-multiSelect>
      </div>

      <!-- a from/to years range instead of fixed options, the draft only applies on approve -->
      <div
        #experienceChip
        class="chip chip--experience"
        [class.chip--active]="experienceRange.value !== null"
      >
        <button
          type="button"
          class="chip__trigger"
          [attr.aria-expanded]="experiencePanelVisible"
          (click)="toggleExperiencePanel()"
        >
          <span class="chip__label">
            @if (experienceRange.value; as range) {
              {{ 'label.WorkExperienceRange' | translate: { from: range.from ?? 0, to: range.to ?? '∞' } }}
            } @else {
              {{ 'label.WorkExperienceYears' | translate }}
            }
          </span>
          @if (experienceRange.value !== null) {
            <svg-icon
              class="chip__clear"
              src="/assets/icons/close-small.svg"
              [svgClass]="'ico-clear'"
              (click)="$event.stopPropagation(); onExperienceReset()"
            ></svg-icon>
          }
          <svg-icon class="chip__caret" src="/assets/icons/arrow-down.svg" [svgClass]="'ico-caret'"></svg-icon>
        </button>

        @if (experiencePanelVisible) {
          <div class="filter-panel" (keydown.escape)="experiencePanelVisible = false">
            <div class="experience-range">
              <input
                type="number"
                min="0"
                max="50"
                class="experience-range__input"
                [formControl]="experienceFrom"
                [placeholder]="'label.From' | translate"
                (keydown.enter)="$event.preventDefault(); onExperienceApprove()"
              />
              <span class="experience-range__separator">-</span>
              <input
                type="number"
                min="0"
                max="50"
                class="experience-range__input"
                [formControl]="experienceTo"
                [placeholder]="'label.To' | translate"
                (keydown.enter)="$event.preventDefault(); onExperienceApprove()"
              />
            </div>

            <div class="dropdown-footer">
              <button type="button" class="dropdown-footer__reset" (click)="onExperienceReset()">
                {{ 'label.ClearFilter' | translate }}
              </button>
              <button type="button" class="dropdown-footer__approve" (click)="onExperienceApprove()">
                {{ 'label.Approve' | translate }}
              </button>
            </div>
          </div>
        }
      </div>

      <!-- option 0 is the "any salary" lookup entry, so it does not count as a chosen value -->
      <div
        class="chip chip--salary"
        [class.chip--active]="salaryRangeOptionId.value != null && salaryRangeOptionId.value !== 0"
      >
        <p-select
          #salarySelect
          ngSkipHydration
          formControlName="salaryRangeOptionId"
          [options]="data.salaryOptions"
          optionLabel="name"
          optionValue="key"
          [placeholder]="'label.SalaryMain' | translate"
          [showClear]="true"
          [styleClass]="'chip__dropdown'"
          [panelStyleClass]="'dropdown-panel'"
          [appendTo]="'self'"
        >
          <!-- single choice, but drawn as the same check list as the other panels -->
          <ng-template pTemplate="item" let-item>
            <span class="dropdown-check"></span>
            <span class="dropdown-item">{{ item.name }}</span>
          </ng-template>

          <ng-template #dropdownicon>
            <svg-icon src="/assets/icons/arrow-down.svg" [svgClass]="'ico-caret'"></svg-icon>
          </ng-template>

          <ng-template #clearicon>
            <svg-icon src="/assets/icons/close-small.svg" [svgClass]="'ico-clear'"></svg-icon>
          </ng-template>

          <ng-template #footer>
            <div class="dropdown-footer">
              <button
                type="button"
                class="dropdown-footer__reset"
                (click)="salaryRangeOptionId.setValue(null); salarySelect.hide()"
              >{{ 'label.ClearFilter' | translate }}</button>
              <button
                type="button"
                class="dropdown-footer__approve"
                (click)="salarySelect.hide()"
              >{{ 'label.Approve' | translate }}</button>
            </div>
          </ng-template>
        </p-select>
      </div>

    </div>

    <div
      class="search-panel__today"
      (click)="onTodayFilter(data.todayPublishDateOption, data.publishDateOptions)"
    >
      <span>{{ 'label.Today' | translate }}</span>
      <span>{{ data.initialData?.todayAnnouncementCount }}</span>
    </div>

    <button
      type="button"
      class="search-panel__submit"
      (click)="onSearchButtonClick(data.salaryOptions, data.publishDateOptions)"
    >
      <span class="search-panel__submit-text">{{ 'label.Search' | translate }}</span>
      <span class="search-panel__submit-count">
        [{{ data.initialData?.announcementCount }}
        {{ 'label.Announcement' | translate: { count: data.initialData?.announcementCount } }}]
      </span>
    </button>

  </div>

</form>

}
